import { useState } from "react";
import useReveal from "@/hooks/useReveal";
import BeforeAfter from "./BeforeAfter";
import { featuredCases } from "@/mocks/portfolio";

export default function Portfolio() {
  const [active, setActive] = useState(0);
  const current = featuredCases[active];
  const ref = useReveal<HTMLDivElement>();

  return (
    <section id="portfolio" className="relative w-full py-24 md:py-32 bg-white overflow-hidden">
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-neutral-50/70 via-white to-white" />
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-point-50/12 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-neutral-100/40 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="w-full px-6 md:px-12 lg:px-16 relative z-10">
        <div ref={ref} className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-point-500"></span>
              <span className="text-point-600 text-xs font-semibold uppercase tracking-[0.3em]">
                Featured Projects
              </span>
            </div>
            <h2 className="font-hero text-navy-900 text-3xl md:text-5xl lg:text-6xl leading-tight mb-6">
              실제 시공사례
            </h2>
            <p className="text-neutral-600 text-base md:text-lg leading-relaxed">
              슬라이드를 좌우로 움직여 시공 전과 후를 직접 비교해보세요.
              완공 사진이 아닌 실제 현장 데이터를 그대로 공개합니다.
            </p>
          </div>
          <a
            href="https://blog.naver.com/ksh1009000"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex self-start items-center gap-3 px-7 py-3.5 border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            전체 시공사례 보기
            <div className="w-5 h-5 flex items-center justify-center transition-transform group-hover:translate-x-1">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {featuredCases.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                active === i
                  ? "bg-navy-900 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              CASE {String(i + 1).padStart(2, "0")}
            </button>
          ))}
        </div>

        {/* Before / After */}
        <BeforeAfter before={current.before} after={current.after} />

        {/* Meta */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-point-50 text-point-700 text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-point-500"></span>
              {current.category}
            </div>
            <h3 className="font-hero text-navy-900 text-2xl md:text-3xl leading-tight mb-4">
              {current.title}
            </h3>
            <p className="text-neutral-600 text-base leading-relaxed max-w-2xl">
              {active === 0
                ? "부산광역시 북구 어린이보호구역 내 횡단보도 및 도로 표시를 선명한 노란색 도로용 페인트로 도색 정비한 공사입니다. 기존 흰색 표시가 마모되어 시인성이 저하된 구간을 노란색으로 전면 재도색하였으며, 어린이 통학 안전을 강화하고 운전자의 서행 준수를 유도하는 효과를 높였습니다. 선명한 황색 노면 표시로 어린이보호구역 인지도를 대폭 향상시켰습니다."
                : active === 1
                ? "CCTV통합관제센터는 구민의 안전을 책임지는 공공 관제시설로, 노후화된 내부 환경을 전면 개선하는 구조환경개선공사를 진행하였습니다. 천장·바닥·벽체·전기·설비를 포함한 전체 리모델링을 통해 쾌적하고 기능적인 관제센터로 탈바꿈하였습니다. 공사 전 낡고 어두웠던 공간이 밝고 정돈된 스마트 관제환경으로 완성되었습니다."
                : active === 2
                ? "신일화학 음성공장은 화학원료를 취급하는 공장으로 바닥 표면의 내화학성과 내마모성이 핵심 요건인 현장입니다. 노후 콘크리트 바닥에 에폭시 슬라이스 공법을 적용하여 라이닝 0.5MM + 코팅 0.1MM로 마감하였으며, 삼화페인트 에폭시라이닝 제품으로 선명한 파란색 광택 바닥을 완성하였습니다. 작업 환경이 대폭 개선되고 청결 유지가 쉬운 쾌적한 공장 바닥으로 탈바꿈하였습니다."
                : active === 3
                ? "영케미칼 양산공장은 의료용품 생산시설로 위생과 내구성이 최우선인 현장입니다. 기존 데코타일을 전면 제거하고 스키로더 연삭장비로 바닥 표면을 완전히 정리한 후, DNC에폭시 라이닝 3MM을 도포하여 유리와 같은 광택 표면을 완성하였습니다. 의료용품 생산환경에 적합한 내오염성·내약품성이 우수한 바닥으로 탈바꿈하였습니다."
                : active === 4
                ? "덕천아트빌라일원 옹벽경관개선사업은 노후화된 옹벽의 환경을 정비하여 쾌적한 도시 미관을 확보하기 위한 공사입니다. 기존 옹벽 표면을 고압세척으로 완전히 정리한 후 이형타일을 시공하고, 중성화방지도료로 마감하여 옹벽의 내구성 향상과 함께 도시 경관을 아름답게 개선하였습니다."
                : active === 5
                ? "제일화성 온산공장은 화학공장 특성상 외벽 판넬 전반에 걸쳐 심각한 부식이 발생한 상태였습니다. 부식 구간은 글라인더로 완전 제거 후 보강작업을 실시하고, 멀티에폭시 프라이머 도장 후 중성화방지도료를 도장하여 내화학성과 내후성을 극대화하였습니다."
                : active === 6
                ? "정관보건지소 옥상·옥탑은 기존 우레탄 도막이 전면 들뜸 현상으로 방수 기능을 상실한 상태였습니다. 스키로더(밥캣)를 활용하여 기존 우레탄 도막을 완전 제거한 후, 우레탄 프라이머 → 우레탄 방수제 3MM → 우레탄 상도 순으로 시공하였으며, 특히 배수로·트랜치 구간은 집중 방수처리하여 누수를 원천 차단하였습니다."
                : active === 7
                ? "부전인삼시장 공용화장실은 장기간 누수로 인해 슬라브층까지 손상이 진행된 상태였습니다. 기존 마감재와 슬라브를 전면 철거한 후 철저한 방수층 시공을 완료하였으며, 위생도기·타일·천장을 모두 교체하여 깔끔하게 리모델링하였습니다."
                : active === 8
                ? "글로벌10003호는 노후화된 선체 외판 및 데크 전면의 도막 노화와 방청 보호를 위해 선박용 에폭시 전용 도장공사를 진행하였습니다. 데크는 PPG 에폭시 코팅제, 외판은 헴펠코리아 제품을 적용하여 해수염 환경에서의 내구성과 방식성을 극대화하였습니다."
                : active === 9
                ? "동구노인복지관 분관은 노후된 드라이비트 외벽의 오염과 변색, 미세균열이 발생하여 외부 도장공사를 진행하였습니다. 드라이비트 외벽은 전용 보수 및 도장 공법을 적용해야 균열 재발과 도막 박리를 최소화할수 있습니다."
                : "슬라이드를 좌우로 움직여 시공 전과 후를 직접 비교해보세요. 완공 사진이 아닌 실제 현장 데이터를 그대로 공개합니다."
              }
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { icon: "ri-calendar-line", label: "공사기간", value: current.period },
              { icon: "ri-map-pin-line", label: "현장위치", value: current.location },
              { icon: "ri-tools-line", label: "공법", value: current.method },
              { icon: "ri-price-tag-3-line", label: "사용제품", value: current.product },
            ].map((item) => (
              <div
                key={item.label}
                className="p-5 border border-neutral-200 rounded-xl hover:border-navy-800 transition-all duration-500 ease-out hover:-translate-y-1"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-point-500 text-base`}></i>
                  </div>
                  <div className="text-xs text-neutral-500 uppercase tracking-widest">
                    {item.label}
                  </div>
                </div>
                <div className="text-sm text-navy-900 font-medium leading-snug">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}